import type { FC, MouseEvent } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Avatar, Box } from '@mui/material'

const USER_AVATAR_SIZES = {
  small: { width: 24, height: 24, fontSize: '0.75rem' },
  medium: { width: 36, height: 36, fontSize: '0.875rem' },
  large: { width: 48, height: 48, fontSize: '1rem' },
} as const

type UserAvatarSize = keyof typeof USER_AVATAR_SIZES

interface UserAvatarProps {
  readonly initials: string
  readonly size?: UserAvatarSize
  readonly onClick?: (event: MouseEvent<HTMLElement>) => void
  readonly muted?: boolean
}

export const UserAvatar: FC<UserAvatarProps> = ({
  initials,
  size = 'medium',
  onClick,
  muted = false,
}) => {
  const sizeConfig = USER_AVATAR_SIZES[size]

  const avatarSx: SxProps<Theme> = {
    ...sizeConfig,
    bgcolor: muted ? 'text.disabled' : 'primary.main',
    opacity: muted ? 0.6 : 1,
    fontWeight: 600,
    transition: 'opacity 200ms ease-in-out',
  }

  if (!onClick) {
    return <Avatar sx={avatarSx}>{initials}</Avatar>
  }

  return (
    <Box component="button" onClick={onClick} sx={buttonContainerSx}>
      <Avatar sx={avatarSx}>{initials}</Avatar>
    </Box>
  )
}

const buttonContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  borderRadius: '50%',
  transition: 'transform 150ms ease-in-out',
  '&:hover': {
    transform: 'scale(1.05)',
  },
}
